/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 */
import React from "react";
import { useTranslation } from "react-i18next";
import CustomCard from "../../../../components/CustomCard";
import CustomChip from "../../../../components/CustomChip";
import CustomTooltip from "../../../../components/CustomTooltip";
import { CustomCheckbox } from "../../../../components/CustomCheckbox";
import {
    EmailSubsectionChip,
    IconCheckboxChecked,
    IconCheckboxUnchecked,
    IconCloseChip,
    IconTitleEntryMethod,
    PersonChip,
} from "../../../../components/buttons/IconButtons";
import { RELATIONSHIP_TYPE_CHIP, RESIDENT_STATUS_CHIP, RESIDENT_TYPE_CHIP } from "../../Model/Model";
import {colors} from "../../../../enums/colors";

/**
 * Assigned resident card: full name with its verification status chip
 * (separated by a vertical divider), email and apartment.
 *
 * @param {Object} props - Component properties
 * @param {string} props.keyId - Unique identifier key for the item
 * @param {Object} props.item - Resident data to display
 * @param {string|number} props.item[keyId] - Unique identifier value
 * @param {string} props.item.fullName - Resident full name
 * @param {string} props.item.email - Resident email address
 * @param {string} [props.item.apartment] - Apartment assigned to the resident, rendered with the "apt" prefix (optional)
 * @param {string} [props.item.status] - RESIDENT_TYPE or RESIDENT_STATUS value rendered as a chip
 *   next to the name
 * @param {string} [props.item.relationship] - RELATIONSHIP_TYPE value rendered translated as a
 *   chip next to the email, any other value is shown as it comes (optional)
 * @param {boolean} props.item.disabled - Indicates if the item is disabled
 * @param {boolean} [props.item.checked] - Indicates if the item is selected (checkbox state)
 * @param {Function} [props.onDelete] - Callback executed when the delete action is triggered
 * @param {Function} [props.handleCheckboxChange] - Optional handler for checkbox state changes
 * @param {JSX.Element} [props.actions] - Actions menu rendered on the top right corner of the card
 * @param {JSX.Element|Object} [props.statusChip] - Chip rendered next to the name. It accepts a ready
 *   element to render as it is, or a CustomChip config ({ translation, background, fontColor,
 *   borderColor, icon, classNameIcon, variant }). Defaults to the RESIDENT_TYPE_CHIP or the
 *   RESIDENT_STATUS_CHIP of item.status
 *
 * @returns {JSX.Element} Resident card component with selection and delete functionality
 */
const ResidentAssignedCard = (
    {
        keyId,
        item,
        onDelete,
        handleCheckboxChange,
        actions,
        statusChip
    }
) => {
    const { t } = useTranslation();
    // The caller can send its own chip, otherwise the status is read as a resident
    // type and, when it is not one of them, as a verification status
    const chip = statusChip || RESIDENT_TYPE_CHIP[item?.status] || RESIDENT_STATUS_CHIP[item?.status];
    // RELATIONSHIP_TYPE values are translated, any other one is free text
    // the resident typed and is rendered as it comes
    const relationshipChip = RELATIONSHIP_TYPE_CHIP[item?.relationship];
    const relationshipLegend = relationshipChip ? t(relationshipChip.translation) : item?.relationship;

    return (
        <CustomCard
            classes="w-100"
            classCard="card-border-8px"
            classContent={`background-shades-50`}
        >
            <div className="row">
                <div className={"d-flex justify-content-between p-0"}>
                    { handleCheckboxChange &&
                        <div className={"align-top mr-3"}>
                            <CustomCheckbox
                                id={'cb_rac_row_' + keyId}
                                checked={item?.checked}
                                onChange={(e) => {
                                    handleCheckboxChange({
                                        target: {
                                            [keyId]: item[keyId],
                                            checked: e.target.checked,
                                        },
                                    });
                                }}
                                disabled={item?.disabled}
                                icon={<IconCheckboxUnchecked className={"icon-color-general-950"}/>}
                                checkedIcon={<IconCheckboxChecked className={"icon-color-shades-800"}/>}
                                disableRipple={true}
                            />
                        </div>
                    }

                    <div className={"flex-grow-1 minw-0 mr-1 mt-2"}>
                        <div className="d-flex align-items-center justify-content-between">
                            <div className={`col-12 p-0`}>
                                <div className='d-flex align-items-center justify-content-start'>
                                    <div className={"d-flex align-items-center"}><PersonChip className="mr-8px mb-1" /></div>
                                    <div className={`${onDelete ? 'mr-5' : ''} flex-grow-1 minw-0 p-0`}>
                                        <CustomTooltip
                                            text={item?.fullName}
                                            classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis-1-row `}
                                            showIfTruncated={true}
                                        />
                                    </div>
                                    { chip &&
                                        <>
                                            <div className='vertical-separator-shades-950' />
                                            <div className={"flex-shrink-0"}>
                                                { React.isValidElement(chip) ? chip :
                                                    <CustomChip
                                                        background={chip.background}
                                                        fontColor={chip.fontColor}
                                                        borderColor={chip.borderColor}
                                                        legend={t(chip.translation)}
                                                        variant={chip.variant || 'large'}
                                                        icon={chip.icon}
                                                        classNameIcon={chip.classNameIcon}
                                                    />
                                                }
                                            </div>
                                        </>
                                    }
                                    { onDelete &&
                                        <div className={"position-absolute r-0px t-negative-4px"}>
                                            <IconCloseChip
                                                className='icon-color-shades-400 icon-hover-shades-950'
                                                onClick={() => onDelete(item)}
                                            />
                                        </div>
                                    }
                                    { actions &&
                                        <div className={"flex-shrink-0 ml-2"}>{actions}</div>
                                    }
                                </div>
                            </div>
                        </div>

                        <div className="border-bottom-shades-300 my-2" />

                        <div className='d-flex align-items-center justify-content-between '>
                            <div className={`${ (item?.apartment || item?.relationship) ? 'col-6' : 'col-12' } pl-0`}>
                                <div className="d-flex align-items-center">
                                    <div className="d-flex align-items-center"><EmailSubsectionChip className="mr-8px flex-shrink-0" /></div>
                                    <div className={"minw-0"}>
                                        <CustomTooltip
                                            text={item?.email}
                                            classes={`fs-14px font-color-general-950 text-ellipsis-1-row`}
                                            showIfTruncated={true}
                                        />
                                    </div>
                                </div>
                            </div>
                            {item?.apartment && <div className='col-6 px-0'>
                                <div className="d-flex align-items-center">
                                    <div className="d-flex align-items-center"><IconTitleEntryMethod className="mr-8px flex-shrink-0" width={16} height={16}/></div>
                                    <div className={"minw-0"}>
                                        <CustomTooltip
                                            text={`${t("apt")} ${item?.apartment}`}
                                            classes={`fs-14px font-color-general-950 text-ellipsis-1-row`}
                                            showIfTruncated={true}
                                        />
                                    </div>
                                </div>
                            </div>
                            }

                            {item?.relationship && <div className='col-6 px-0'>
                                <div className="d-flex align-items-center justify-content-end">
                                    <CustomChip
                                        legend={relationshipLegend}
                                        background={relationshipChip?.background || colors.secondary100}
                                        borderColor={relationshipChip?.borderColor || colors.secondary150}
                                        fontColor={relationshipChip?.fontColor || colors.secondary700}
                                        variant={"compact"}
                                        classNameIcon={"icon-color-secondary-700"}
                                    />
                                </div>
                            </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </CustomCard>
    );
}

export default ResidentAssignedCard;
